import AppLayout from "@/app/AppLayout";
import { useAuth } from "@/features/auth/AuthContext";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import { usePaymentAccount } from "@/features/settings/hooks/usePaymentAccount";
import { usePushNotifications } from "@/features/settings/hooks/usePushNotifications";
import { AsyncBoundary, messageForPartner, PageHeader } from "@/ui/patterns";
import { BusinessProfileForm } from "./components/BusinessProfileForm";
import { PaymentAccountSection } from "./components/PaymentAccountSection";
import { PaymentAccountUnavailableNotice } from "./components/PaymentAccountUnavailableNotice";
import { PushNotificationsSection } from "./components/PushNotificationsSection";
import type { PaymentAccount } from "@/domain/payment-account/types";

/** A failed request is not an answer: the invitation to found a podnik would
 *  otherwise head a screen belonging to a partner who already has one. */
function businessTitle(hasBusiness: boolean, error: unknown) {
  if (error) return "Podnik";
  return hasBusiness ? "Váš podnik" : "Založte podnik";
}

export default function SettingsPage() {
  const { business, hasBusiness, isLoading, error, refetch: refetchBusiness } = useUserBusiness();
  const { user } = useAuth();
  const {
    account,
    isLoading: isLoadingAccount,
    error: accountError,
    enabled: accountEnabled,
    refetch: refetchAccount,
    isStripeHandoffUnderway,
    startOnboarding,
    createAccount,
  } = usePaymentAccount();
  const push = usePushNotifications();

  return (
    <AppLayout>
      <div className="max-w-2xl space-y-6">
        <PageHeader
          breadcrumbs={
            <p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">
              Nastavení
            </p>
          }
          title={businessTitle(hasBusiness, error)}
        />
        <AsyncBoundary
          data={isLoading ? undefined : { business }}
          isLoading={isLoading}
          error={error}
          onRetry={refetchBusiness}
        >
          {({ business: b }) => (
            <BusinessProfileForm business={b} loginEmail={user?.email ?? null} />
          )}
        </AsyncBoundary>

        {/* The account is read by business, so a failed business fetch leaves
            nothing to ask for: the boundary above already carries that failure,
            and its retry is the one that can undo it. */}
        {hasBusiness && (
          <AsyncBoundary<PaymentAccount>
            data={account}
            isLoading={isLoadingAccount}
            error={accountError}
            enabled={accountEnabled}
            onRetry={refetchAccount}
            errorFallback={(error, retry) => (
              <PaymentAccountUnavailableNotice onRetry={retry} reason={messageForPartner(error)} />
            )}
          >
            {(loadedAccount) => (
              <PaymentAccountSection
                account={loadedAccount}
                isStripeHandoffUnderway={isStripeHandoffUnderway}
                onStartOnboarding={startOnboarding}
                onCreateAccount={createAccount}
                onRefresh={refetchAccount}
              />
            )}
          </AsyncBoundary>
        )}

        <PushNotificationsSection
          state={push.state}
          isSwitching={push.isSwitching}
          onEnable={() => void push.enable()}
          onDisable={() => void push.disable()}
        />
      </div>
    </AppLayout>
  );
}
